import { zodResolver } from '@hookform/resolvers/zod';
import { useCallback, useRef } from 'react';
import { useForm, type UseFormReturn } from 'react-hook-form';
import type { ZodSchema } from 'zod';
import type { z } from 'zod';

export type UseFormOptions<T> = {
  /** for default options */
  defaultValues?: Partial<T>;

  /** called when the form is valid and submitted */
  onSubmit?: (values: T) => void;
  /** When form.reset() is called with no args, values come from this. Consumer-owned (e.g. useContactForm). */
  getResetValues?: () => Partial<T>;
};

/** Options for useFormUtil: schema drives the form value type T. */
type UseFormUtilOptions<TSchema extends ZodSchema> = UseFormOptions<z.infer<TSchema>> & {
  zodSchema: TSchema;
};

/** Return type of useFormUtil: UseFormReturn plus onValidSubmit, isValid, values. */
type UseFormUtilReturn<TSchema extends ZodSchema> = UseFormReturn<z.infer<TSchema>> & {
  onValidSubmit: (e?: React.BaseSyntheticEvent) => Promise<void>;
  isValid: boolean;
  values: z.infer<TSchema>;
};

/**
 * Reusable form hook: infers form value type from zodSchema.
 * Use in domain hooks (e.g. useContactCreateForm) for typed form + onValidSubmit.
 * reset() with no args: resets to getResetValues() if provided, else defaultValues.
 */
export function useFormUtil<TSchema extends ZodSchema>(
  options: UseFormUtilOptions<TSchema>,
): UseFormUtilReturn<TSchema> {
  type T = z.infer<TSchema>;
  const { zodSchema, defaultValues, onSubmit, getResetValues } = options;

  const form = useForm<T>({
    resolver: zodResolver(zodSchema),
    defaultValues: defaultValues as T | undefined,
  });

  const getResetValuesRef = useRef(getResetValues);
  const defaultValuesRef = useRef(defaultValues);
  getResetValuesRef.current = getResetValues;
  defaultValuesRef.current = defaultValues;

  const reset = useCallback(
    (...args: Parameters<UseFormReturn<T>['reset']>) => {
      if (args.length === 0 || args[0] === undefined) {
        const values = getResetValuesRef.current
          ? getResetValuesRef.current()
          : defaultValuesRef.current ?? {};
        form.reset(values as T);
      } else {
        form.reset(...args);
      }
    },
    [form],
  );

  function onValidSubmit(values: T) {
    onSubmit?.(values);
  }

  return {
    ...form,
    reset,
    onValidSubmit: form.handleSubmit(onValidSubmit),
    isValid: form.formState.isValid,
    values: form.getValues(),
  };
}
